/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { CommonModule, DOCUMENT } from "@angular/common";
import { Component, inject, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { EChartsOption, EChartsType } from "echarts";
import { BehaviorSubject, defer, forkJoin, map, Observable, Subject, tap } from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { MixedChart } from "../../../shared/models/http/charts/mixed-chart/mixed-chart.model";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { CioSummary } from "../../../shared/models/http/dashboard/cio-summary.model";
import { HardwareAssetsRealtimeInformation } from "../../../shared/models/http/dashboard/hardware-assets-realtime-information.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { ChartPattern, ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { CioDetailChart } from "../../../shared/models/ui/cio-detail-chart.model";
import { CioMode } from "../../../shared/models/ui/cio-mode.model";
import { ReportingPeriodValue } from "../../../shared/models/ui/reporting-period.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { MaxDecimalsPipe } from "../../../shared/pipes/max-decimals.pipe";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardCioService } from "../../../shared/services/dashboard-cio.service";
import { DashboardImpactService } from "../../../shared/services/dashboard-impact.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { ToastService } from "../../../shared/services/toast.service";
import { CIO_CHIPS_BY_SCOPE, CIO_SWITCH_OPTIONS } from "../../../shared/utils/constants";
import {
	CHART_PATTERNS,
	DETAIL_CHARTS_BY_SCOPE_COLORS,
	DETAIL_CHARTS_BY_SCOPE_OVERRIDES,
	EMISSIONS_SHARE_COLORS_BY_SCOPE,
	EMISSIONS_SHARE_COLORS_BY_TYPE,
	FORECAST_MIXED_CHART_COLORS_BY_SCOPE,
	FORECAST_MIXED_CHART_COLORS_BY_TYPE,
	FORECAST_MIXED_CHART_OVERRIDES_BY_SCOPE,
	FORECAST_MIXED_CHART_OVERRIDES_BY_TYPE,
	FORECAST_MIXED_CHART_PATTERNS_BY_SCOPE,
	FORECAST_MIXED_CHART_PATTERNS_BY_TYPE
} from "../../../shared/utils/echarts/chart-constants";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import {
	getDefaultReportingPeriodValue,
	getReportingPeriodOptions,
	getReportingPeriodRange
} from "../../../shared/utils/reporting-period";
import { formatDateForBE, saveAs } from "../../../shared/utils/utils";

@Component({
	selector: "globalictcc-impact",
	standalone: true,
	imports: [
		CommonModule,
		DropdownComponent,
		ButtonComponent,
		KpiComponent,
		ChartComponent,
		MaxDecimalsPipe,
		ReactiveFormsModule,
		ChipsComponent
	],
	templateUrl: "./impact.component.html",
	styleUrl: "./impact.component.scss"
})
export class ImpactComponent {
	// F.Dore: issue #1401 --> Will never change for now, but kept as variable in case of future expansion to allow switching between "TYPE" and "SCOPE".
	public mode: CioMode = "TYPE";
	public readonly grants = GRANTS.impact;
	public periodControl = new FormControl<ReportingPeriodValue>(getDefaultReportingPeriodValue());
	public switchOptions: SelectOption[] = CIO_SWITCH_OPTIONS;
	public periodOptions: SelectOption[] = getReportingPeriodOptions();
	public chips: ChipOption[] = CIO_CHIPS_BY_SCOPE;

	public otherTerms?: HardwareAssetsRealtimeInformation;

	public forecastMixedChartColorsByType: string[] = FORECAST_MIXED_CHART_COLORS_BY_TYPE;
	public forecastMixedChartColorsByScope: string[] = FORECAST_MIXED_CHART_COLORS_BY_SCOPE;
	public forecastMixedChartOverridesByType: EChartsOption =
		FORECAST_MIXED_CHART_OVERRIDES_BY_TYPE;
	public forecastMixedChartOverridesByScope: EChartsOption =
		FORECAST_MIXED_CHART_OVERRIDES_BY_SCOPE;
	private _forecastMixedChartOverridesCache?: EChartsOption;
	private _forecastMixedChartOverridesCachedMode?: CioMode;
	public forecastMixedChartPatternsByType: ChartPattern[] = FORECAST_MIXED_CHART_PATTERNS_BY_TYPE;
	public forecastMixedChartPatternsByScope: ChartPattern[] =
		FORECAST_MIXED_CHART_PATTERNS_BY_SCOPE;

	public emissionsSharePieChartColorsByType: string[] = EMISSIONS_SHARE_COLORS_BY_TYPE;
	public emissionsSharePieChartColorsByScope: string[] = EMISSIONS_SHARE_COLORS_BY_SCOPE;

	public selectedDetailChartIndex = 0;
	public detailChartsByScopePatterns: ChartPattern[] = [
		CHART_PATTERNS.TRIANGLES,
		CHART_PATTERNS.DIAGONAL_LINES,
		CHART_PATTERNS.CIRCLES,
		null
	];
	public dataCentersOverviewCards: {
		key: "edc" | "s1s2" | "win";
		label: string;
		value: number | null;
		unit: string;
	}[] = [];
	private _detailChartsByScopeColors: string[][] = DETAIL_CHARTS_BY_SCOPE_COLORS;
	private _detailChartsByScopeOverrides: EChartsOption[] = DETAIL_CHARTS_BY_SCOPE_OVERRIDES;
	private readonly _document: Document = inject(DOCUMENT);

	@ViewChild("chartByType") public emissionsShareByTypeChart?: ChartComponent;
	@ViewChild("chartByScope") public emissionsShareByScopeChart?: ChartComponent;

	private readonly _period$ = new BehaviorSubject(this._getOtherTermsFormOptions());
	private readonly _emissionsShareByTypeRefresh$ = new Subject<void>();
	private readonly _emissionsShareByScopeRefresh$ = new Subject<void>();
	private readonly _forecastRefresh$ = new Subject<void>();
	private readonly _detailChartsRefresh$ = new Subject<void>();

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly summary$: Observable<Loadable<HardwareAssetsRealtimeInformation>> = lazyLoad(
		this._period$,
		range =>
			this._dashboardCioService
				.getSummary(range.startDate, range.endDate)
				.pipe(tap(summary => this._setDataCentersOverviewCards(summary.dataCenters)))
	);

	public readonly emissionsShareByType$: Observable<Loadable<ChartResponse<PieChart>>> = lazyLoad(
		this._period$,
		range =>
			this._dashboardCioService.getEmissionsShare(range.startDate, range.endDate, "TYPE"),
		this._emissionsShareByTypeRefresh$
	);

	public readonly emissionsShareByScope$: Observable<Loadable<ChartResponse<PieChart>>> =
		lazyLoad(
			this._period$,
			range =>
				this._dashboardCioService.getEmissionsShare(
					range.startDate,
					range.endDate,
					"SCOPE"
				),
			this._emissionsShareByScopeRefresh$
		);

	public readonly forecast$: Observable<Loadable<ChartResponse<MixedChart>>> = lazyLoad(
		this._period$,
		range => this._dashboardCioService.getForecast(range.startDate, range.endDate, this.mode),
		this._forecastRefresh$
	);

	public readonly detailCharts$: Observable<Loadable<CioDetailChart[]>> = lazyLoad(
		this._period$,
		range =>
			forkJoin(
				this._getScopeBreakdownParams().map(scope =>
					this._dashboardCioService.getDetails(range.startDate, range.endDate, scope)
				)
			).pipe(
				map(details => this._mapDetailCharts(details)),
				tap(() => (this.selectedDetailChartIndex = 0))
			),
		this._detailChartsRefresh$
	);

	constructor(
		private _dashboardCioService: DashboardCioService,
		private _dashboardImpactService: DashboardImpactService,
		private _ictCarbonInsightsReportService: IctCarbonInsightsReportService,
		private _toastService: ToastService,
		public authorizationService: AuthorizationService
	) {}

	public get forecastMixedChartColors(): string[] {
		return this.mode === "TYPE"
			? this.forecastMixedChartColorsByType
			: this.forecastMixedChartColorsByScope;
	}

	public get forecastMixedChartOverrides(): EChartsOption {
		if (
			this._forecastMixedChartOverridesCache &&
			this._forecastMixedChartOverridesCachedMode === this.mode
		) {
			return this._forecastMixedChartOverridesCache;
		}

		const overrides =
			this.mode === "TYPE"
				? this.forecastMixedChartOverridesByType
				: this.forecastMixedChartOverridesByScope;
		const xAxis = (overrides.xAxis ?? {}) as EChartsOption["xAxis"];
		const axisLabel = {
			...(xAxis as { axisLabel?: Record<string, unknown> }).axisLabel,
			formatter: (value: string | number) => this._formatAxisMonthYear(value)
		};

		this._forecastMixedChartOverridesCachedMode = this.mode;
		this._forecastMixedChartOverridesCache = {
			...overrides,
			xAxis: {
				...(xAxis as Record<string, unknown>),
				axisLabel
			}
		};

		return this._forecastMixedChartOverridesCache;
	}

	private _formatAxisMonthYear(value: string | number): string {
		if (typeof value === "string" && /^\d{2}-\d{4}$/.test(value)) {
			return value;
		}

		const dateValue = typeof value === "number" ? new Date(value) : new Date(value);

		if (Number.isNaN(dateValue.getTime())) {
			return String(value);
		}

		const month = String(dateValue.getUTCMonth() + 1).padStart(2, "0");
		const year = String(dateValue.getUTCFullYear());

		return `${month}-${year}`;
	}

	public get forecastMixedChartPatterns(): ChartPattern[] {
		return this.mode === "TYPE"
			? this.forecastMixedChartPatternsByType
			: this.forecastMixedChartPatternsByScope;
	}

	public handleSelection(selectedChip: string | number) {
		this.selectedDetailChartIndex = selectedChip as number;
	}

	public orderDetailCharts(detailCharts: CioDetailChart[]): CioDetailChart[] {
		const primaryChart = detailCharts[this.selectedDetailChartIndex];
		if (!primaryChart) return detailCharts;

		return [
			primaryChart,
			...detailCharts.filter((_, index) => index !== this.selectedDetailChartIndex)
		];
	}

	public onApply() {
		this._period$.next(this._getOtherTermsFormOptions());
	}

	public handleForecastChartRefresh() {
		this._forecastRefresh$.next();
	}

	public handleEmissionsShareByTypeRefresh() {
		this._emissionsShareByTypeRefresh$.next();
	}

	public handleEmissionsShareByScopeRefresh() {
		this._emissionsShareByScopeRefresh$.next();
	}

	public handleDetailChartsRefresh() {
		this._detailChartsRefresh$.next();
	}

	private _mapDetailCharts(details: BarChart[]) {
		return details.map((chart, index) => {
			const overrides = this._detailChartsByScopeOverrides[index];
			const xAxis = (overrides?.xAxis ?? {}) as EChartsOption["xAxis"];
			const axisLabel = {
				...(xAxis as { axisLabel?: Record<string, unknown> }).axisLabel,
				formatter: (value: string | number) => this._formatAxisMonthYear(value)
			};
			return {
				chart,
				chartOverrides: {
					...overrides,
					xAxis: {
						...(xAxis as Record<string, unknown>),
						axisLabel
					}
				},
				colors: this._detailChartsByScopeColors[0]
			};
		});
	}

	private _getScopeBreakdownParams(): string[] {
		return CIO_CHIPS_BY_SCOPE.map(chip => chip.label.toLowerCase().replace(/\s+/g, ""));
	}

	private _setDataCentersOverviewCards(totals: { label: string; value: number }[] | undefined) {
		const fallbackUnitMap: Record<"edc" | "s1s2" | "win", string> = {
			edc: "kWh",
			s1s2: "kgCO2e",
			win: "m3"
		};
		const cards = [
			{ key: "edc" as const, label: "EDC" },
			{ key: "s1s2" as const, label: "S1 and S2" },
			{ key: "win" as const, label: "WIN" }
		];

		this.dataCentersOverviewCards = cards.map(card => {
			const match = totals?.find(total => this._matchesOverviewLabel(total.label, card.key));
			return {
				key: card.key,
				label: card.label,
				value: match?.value ?? null,
				unit: fallbackUnitMap[card.key]
			};
		});
	}

	private _matchesOverviewLabel(label: string, key: "edc" | "s1s2" | "win") {
		const normalized = label.toLowerCase();
		if (key === "edc") {
			return normalized.includes("edc") || normalized.includes("energy");
		}
		if (key === "win") {
			return normalized.includes("win") || normalized.includes("water");
		}
		return (
			normalized.includes("s1") ||
			normalized.includes("s2") ||
			normalized.includes("emission") ||
			normalized.includes("co2")
		);
	}

	private _mapEmissionsShareWithActuals(
		emissionsShare: ChartResponse<PieChart>,
		summary?: CioSummary
	): ChartResponse<PieChart> {
		const kpis = this.mode === "TYPE" ? summary?.typeKpis : summary?.scopeKpis;
		const slices = emissionsShare?.chart?.PieChartSlice || [];

		if (!kpis?.length || !slices.length) return emissionsShare;

		const kpiLookup = new Map(kpis.map(kpi => [kpi.label.toLowerCase(), kpi]));

		return {
			...emissionsShare,
			chart: {
				...emissionsShare.chart,
				PieChartSlice: slices.map(slice => {
					const matched = kpiLookup.get(slice.name.toLowerCase());
					return {
						...slice,
						actualValue: matched?.value,
						actualUnit: matched?.unit
					};
				})
			}
		};
	}

	private _getOtherTermsFormOptions(): { startDate: string; endDate?: string } | undefined {
		const period = this.periodControl.value || getDefaultReportingPeriodValue();
		const timePeriod = getReportingPeriodRange(period);
		if (!timePeriod?.start) return undefined;

		return {
			startDate: formatDateForBE(timePeriod.start)!,
			endDate: formatDateForBE(timePeriod.end)
		};
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	public onDownloadCsv() {
		const period = this.periodControl.value || getDefaultReportingPeriodValue();
		const timePeriod = getReportingPeriodRange(period);

		const startDate = formatDateForBE(timePeriod?.start)!;
		const endDate = formatDateForBE(timePeriod?.end)!;

		defer(() => {
			this._toastService.success(
				"Download started",
				"Your CSV download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._dashboardImpactService.downloadAsCsv(startDate, endDate);
		})
			.pipe(
				map(blob => URL.createObjectURL(blob)),
				tap({
					next: (url: string) => {
						saveAs(this._document, url, "impact.csv");
						URL.revokeObjectURL(url); // Free the memory
					}
				})
			)
			.subscribe();
	}

	public onExportReport(): void {
		const range = this._getOtherTermsFormOptions();
		if (!range?.endDate) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.IMPACT, {
				from: range.startDate,
				to: range.endDate
			})
			.subscribe();
	}
}
